import { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import { produccionDiariaApi } from '@/services/api'
import type { ConsumoAgregadoRow, ConsumoProduccionRow, MarcasFilas } from '@/types'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { henoArticulo } from '@/utils/filtroArticulo'
import { Loader } from '@/components/common/Loader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import { Pagination } from '@/components/common/Pagination'
import { TableToolbar } from '@/components/common/TableToolbar'
import { BarraMarcas, CabeceraMarca, FilaMarcable, MarcaCelda, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { estadoTraducido } from '@/utils/exportProduccionDiariaExcel'
import { AvisoExport, errorExportDe, type ErrorExport } from '@/components/common/AvisoExport'

const PAGE_SIZE = 50

function fmtQty(n: number) { return n.toLocaleString('es-ES', { maximumFractionDigits: 2 }) }
function fmtDate(iso?: string) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

/** Parsea el desglose "lote:cantidad|lote:cantidad" (nums crudos) → [{lote, qty}]. */
function parseLotesDetalle(s?: string): { lote: string; qty: number }[] {
  if (!s) return []
  return s.split('|').filter(Boolean).map(pair => {
    const i = pair.lastIndexOf(':')  // lastIndexOf: robusto si el código de lote contiene ':'
    return i < 0
      ? { lote: pair, qty: NaN }
      : { lote: pair.slice(0, i), qty: Number(pair.slice(i + 1)) }
  })
}
/** Texto "L01 (300), L02 (200)" para el export. */
function fmtLotesDetalle(s?: string): string {
  return parseLotesDetalle(s)
    .map(x => `${x.lote} (${Number.isFinite(x.qty) ? fmtQty(x.qty) : '—'})`)
    .join(', ')
}

interface Props {
  dark: boolean
  desde: string
  hasta: string
  empresa: string
  isEmpresaAll: boolean
  rows: ConsumoAgregadoRow[]
  isLoading: boolean
  isError: boolean
  q: string[]
  familia: string
}

// ── Detalle perezoso por componente ────────────────────────────────────────────
function DetalleComponente({ dark, componente, desde, hasta, empresa, isEmpresaAll, colSpan, marcas, clavePadre }: {
  dark: boolean; componente: string; desde: string; hasta: string; empresa: string; isEmpresaAll: boolean; colSpan: number
  // Las sublíneas comparten el almacén de marcas de la tabla padre: así el
  // recuento de la franja y el «limpiar» alcanzan también a lo marcado aquí.
  marcas: MarcasFilas
  /** Clave de la fila del componente. La de cada OP empieza por ella, así que
   *  la misma OP marcada bajo un componente no sale marcada bajo otro. */
  clavePadre: string
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['consumos-comp', componente, desde, hasta, empresa],
    queryFn: () => produccionDiariaApi.getConsumosPorComponente(componente, { desde, hasta, empresa: empresa || undefined }),
    staleTime: 10 * 60 * 1000,
  })
  const prods: ConsumoProduccionRow[] = (data?.data ?? []) as ConsumoProduccionRow[]
  const thCls = `text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 text-left whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const tdCls = `text-[11px] px-3 py-1.5 ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const subBg = dark ? 'bg-slate-800/60' : 'bg-slate-50'

  if (isLoading) return <td colSpan={colSpan} className={`px-4 py-3 ${subBg}`}><Loader label={t('production.consumoInvertido.loadingDetail')} size={10} /></td>
  if (isError || !prods.length) return <td colSpan={colSpan} className={`px-4 py-3 ${subBg} text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{isError ? t('production.consumoInvertido.errorDetail') : t('production.consumoInvertido.noDetail')}</td>

  return (
    <td colSpan={colSpan} className={`${subBg} p-0`}>
      <div className="pl-10 pr-4 py-2 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <CabeceraMarca />
              <th className={thCls}>{t('production.consumoInvertido.colOpNum')}</th>
              <th className={thCls}>{t('production.consumoInvertido.colArticle')}</th>
              <th className={thCls}>{t('production.consumoInvertido.colDescription')}</th>
              <th className={thCls}>{t('production.consumoInvertido.colLots')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumoInvertido.colQtyByLot')}</th>
              <th className={thCls}>{t('production.consumoInvertido.colStatus')}</th>
              {isEmpresaAll && <th className={thCls}>{t('production.consumoInvertido.colWarehouse')}</th>}
              <th className={thCls}>{t('production.consumoInvertido.colLastDate')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumoInvertido.colQtyTheoretical')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumoInvertido.colQtyReal')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumoInvertido.colDeviation')}</th>
              <th className={`${thCls} text-right`}>{t('production.consumoInvertido.colDeviationPct')}</th>
              <th className={thCls}>{t('production.consumoInvertido.colUm')}</th>
            </tr>
          </thead>
          <tbody>
            {prods.map((p, i) => {
              // El detalle trae una fila por (componente, empresa, OP): la
              // empresa entra en la clave porque la misma OP puede existir en
              // dos entidades legales.
              const claveOp = claveFila(clavePadre, p.dataareaid, p.numOp)
              return (
              <FilaMarcable key={`${p.numOp}-${i}`} marcas={marcas} clave={claveOp} dark={dark}
                className={dark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                <MarcaCelda marcas={marcas} clave={claveOp} dark={dark} densa />
                <td className={`${tdCls} font-mono font-semibold`}>{p.numOp}</td>
                <td className={`${tdCls} font-semibold`}>{fmtArt(p.articulo)}</td>
                <td className={`${tdCls} max-w-[200px] truncate`} title={p.descripcion}>{p.descripcion}</td>
                <td className={tdCls}>{p.nLotes > 1 ? `${p.nLotes}` : (p.lotes ?? '—')}</td>
                <td className={`${tdCls} text-right`}>
                  {(() => {
                    const items = parseLotesDetalle(p.lotesDetalle)
                    if (!items.length) return '—'
                    return (
                      <div className="flex flex-col items-end gap-0.5">
                        {items.map((x, k) => (
                          <span key={k} className="tabular-nums whitespace-nowrap">
                            {Number.isFinite(x.qty) ? fmtQty(x.qty) : '—'}
                          </span>
                        ))}
                      </div>
                    )
                  })()}
                </td>
                <td className={tdCls}><StatusBadge status={p.estadoOp} dark={dark} namespace="production" /></td>
                {isEmpresaAll && <td className={tdCls}>{p.almacenDestino ?? '—'}</td>}
                <td className={tdCls}>{fmtDate(p.fechaUltima)}</td>
                <td className={`${tdCls} text-right tabular-nums`}>{fmtQty(p.qtyTeorica)}</td>
                <td className={`${tdCls} text-right tabular-nums`}>{fmtQty(p.qtyReal)}</td>
                <td className={`${tdCls} text-right tabular-nums`}>{fmtQty(p.desviacion)}</td>
                <td className={`${tdCls} text-right tabular-nums`}>{p.pctDesviacion.toFixed(1)}%</td>
                <td className={tdCls}>{p.um ?? '—'}</td>
              </FilaMarcable>
              )
            })}
          </tbody>
        </table>
      </div>
    </td>
  )
}

export function ConsumosInvertidoTable({ dark, desde, hasta, empresa, isEmpresaAll, rows, isLoading, isError, q, familia }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)
  const [errorExport, setErrorExport] = useState<ErrorExport | null>(null)
  // Ámbito propio: las marcas de «Consumos» son un conjunto distinto del de
  // «Producto acabado», que agrupa por OP y no por componente.
  const marcas = useMarcasFilas('produccionDiariaConsumos')

  // OR entre términos, AND con la familia. El código del componente entra CRUDO
  // y FORMATEADO: el catálogo del desplegable manda el crudo y la tabla enseña
  // el formateado, y los dos tienen que encontrar la misma fila.
  const lqs = q.map(x => x.toLowerCase())
  const filtered = rows.filter(c => {
    if (familia && c.familiaComponente !== familia) return false
    if (lqs.length === 0) return true
    const texto = henoArticulo(c.componente, fmtArt, c.descripcionComponente)
    return lqs.some(x => texto.includes(x))
  })

  const colCount = 10 // marca + expand + 8 columnas de cabecera (el colSpan cubre la cabecera)
  const cardBg = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const thBase = `px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-500'}`
  const tdBase = `px-3 py-2.5 text-[13px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`

  useEffect(() => { setPage(1) }, [filtered.length])
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  async function exportExcel() {
    if (exporting) return
    setExporting(true)
    setErrorExport(null)
    try {
      const res = await produccionDiariaApi.getConsumosExport({ desde, hasta, empresa: empresa || undefined })
      const visibles = new Set(filtered.map(c => c.componente))
      const data = (res.data ?? []).filter(p => visibles.has(p.componente)).map(p => ({
        [t('production.consumoInvertido.colComponent')]:      fmtArt(p.componente),
        [t('production.consumoInvertido.colOpNum')]:          p.numOp,
        [t('production.consumoInvertido.colArticle')]:        fmtArt(p.articulo),
        [t('production.consumoInvertido.colDescription')]:    p.descripcion,
        [t('production.consumoInvertido.colLots')]:           p.lotes ?? '',
        [t('production.consumoInvertido.colQtyByLot')]:       fmtLotesDetalle(p.lotesDetalle),
        [t('production.consumoInvertido.colStatus')]:         estadoTraducido(t, p.estadoOp),
        [t('production.consumoInvertido.colWarehouse')]:      p.almacenDestino ?? '',
        [t('production.consumoInvertido.colLastDate')]:       p.fechaUltima ?? '',
        [t('production.consumoInvertido.colQtyTheoretical')]: p.qtyTeorica,
        [t('production.consumoInvertido.colQtyReal')]:        p.qtyReal,
        [t('production.consumoInvertido.colDeviation')]:      p.desviacion,
        [t('production.consumoInvertido.colDeviationPct')]:   p.pctDesviacion,
        [t('production.consumoInvertido.colUm')]:             p.um ?? '',
      }))
      const ws = XLSX.utils.json_to_sheet(data)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, t('production.consumoInvertido.exportSheet'))
      XLSX.writeFile(wb, `${t('production.consumoInvertido.exportFilename')}_${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (e) {
      setErrorExport(errorExportDe(e))
    } finally {
      setExporting(false)
    }
  }

  function toggle(comp: string) {
    setExpanded(prev => { const s = new Set(prev); s.has(comp) ? s.delete(comp) : s.add(comp); return s })
  }

  if (isLoading) {
    return (
      <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg} p-4 space-y-2`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={`h-10 rounded-lg animate-pulse ${dark ? 'bg-white/4' : 'bg-slate-100'}`} style={{ opacity: 1 - i * 0.09 }} />
        ))}
      </div>
    )
  }
  if (isError || !rows.length) {
    return <div className={`rounded-xl border ${cardBg}`}><EmptyState dark={dark} title={t('production.noResultsTitle')} description={t('production.noResultsSub')} /></div>
  }
  if (!filtered.length) {
    return <div className={`rounded-xl border ${cardBg}`}><EmptyState dark={dark} title={t('production.noResultsTitle')} description={t('production.noResultsSub')} /></div>
  }

  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>
      <TableToolbar dark={dark}
        left={<span className={`text-[11px] font-semibold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{filtered.length.toLocaleString('es-ES')} {t('production.consumoInvertido.components')}</span>}
        right={
          <span className="flex items-center gap-2">
            <AvisoExport error={errorExport} dark={dark} />
            <button onClick={exportExcel} disabled={exporting} className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-medium cursor-pointer ${exporting ? 'opacity-50 cursor-not-allowed' : ''} ${dark ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm'}`}>{t('production.consumoInvertido.export')}</button>
          </span>
        }
      />
      <BarraMarcas dark={dark} marcas={marcas} />
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead className={dark ? 'bg-slate-800/50' : 'bg-slate-50/80'}>
            <tr>
              <CabeceraMarca />
              <th className={`${thBase} w-8`} />
              <th className={thBase}>{t('production.consumoInvertido.colComponent')}</th>
              <th className={thBase}>{t('production.consumoInvertido.colDescription')}</th>
              <th className={thBase}>{t('production.consumoInvertido.colFamily')}</th>
              <th className={`${thBase} text-right`}>{t('production.consumoInvertido.colQtyTheoretical')}</th>
              <th className={`${thBase} text-right`}>{t('production.consumoInvertido.colQtyReal')}</th>
              <th className={`${thBase} text-right`}>{t('production.consumoInvertido.colDeviation')}</th>
              <th className={`${thBase} text-right`}>{t('production.consumoInvertido.colDeviationPct')}</th>
              <th className={thBase}>{t('production.consumoInvertido.colUm')}</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(c => {
              const isOpen = expanded.has(c.componente)
              const rowBg = dark ? 'hover:bg-white/3' : 'hover:bg-slate-50/60'
              // La tabla agrega por componente, así que el código de componente
              // identifica la fila y la marca aguanta filtrar y paginar.
              const clave = claveFila(c.componente)
              return (
                <Fragment key={c.componente}>
                  <FilaMarcable marcas={marcas} clave={clave} dark={dark}
                    className={`${rowBg} border-t ${dark ? 'border-white/4' : 'border-slate-100'}`}>
                    <MarcaCelda marcas={marcas} clave={clave} dark={dark} />
                    <td className={`${tdBase} pl-4 pr-1 w-8`}>
                      <button onClick={() => toggle(c.componente)} className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer ${dark ? 'text-slate-500 hover:text-slate-200 hover:bg-white/8' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}>
                        <svg className="w-3 h-3" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                          {isOpen ? <path d="M2 4l3 3 3-3" /> : <path d="M4 2l3 3-3 3" />}
                        </svg>
                      </button>
                    </td>
                    <td className={`${tdBase} font-mono font-semibold`}>{fmtArt(c.componente)}</td>
                    <td className={`${tdBase} max-w-[240px] truncate`} title={c.descripcionComponente}>{c.descripcionComponente}</td>
                    <td className={tdBase}>{c.familiaComponente ?? '—'}</td>
                    <td className={`${tdBase} text-right tabular-nums`}>{fmtQty(c.qtyTeorica)}</td>
                    <td className={`${tdBase} text-right tabular-nums`}>{fmtQty(c.qtyReal)}</td>
                    <td className={`${tdBase} text-right tabular-nums`}>{fmtQty(c.desviacion)}</td>
                    <td className={`${tdBase} text-right tabular-nums`}>{c.pctDesviacion.toFixed(1)}%</td>
                    <td className={tdBase}>{c.um ?? '—'}</td>
                  </FilaMarcable>
                  {isOpen && (
                    <tr className={dark ? 'bg-slate-800/40 border-t border-white/4' : 'bg-slate-50/80 border-t border-slate-100'}>
                      <DetalleComponente dark={dark} componente={c.componente} desde={desde} hasta={hasta} empresa={empresa} isEmpresaAll={isEmpresaAll} colSpan={colCount} marcas={marcas} clavePadre={clave} />
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination dark={dark} page={safePage} totalPages={totalPages} totalItems={filtered.length}
          itemLabel={t('production.consumoInvertido.components')} pageLabel={t('common.page')} ofLabel={t('common.of')}
          onFirst={() => setPage(1)} onPrev={() => setPage(safePage - 1)} onNext={() => setPage(safePage + 1)} onLast={() => setPage(totalPages)} />
      )}
    </div>
  )
}
